/**
 * Canvas diagnostics utility.
 *
 * - Collects comprehensive canvas, GPU, memory, display, and performance data.
 * - Exposed on `window.__canvasDiagnostics` so support engineers can call it
 *   from the browser console on any environment (including production).
 * - Used internally by the GlobeLayer watchdog to send rich context to Sentry
 *   when a blank-canvas event is detected.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CanvasInfo {
  /** CSS-layout size reported by getBoundingClientRect */
  cssWidth: number;
  cssHeight: number;
  /** Internal bitmap buffer dimensions */
  bufferWidth: number;
  bufferHeight: number;
  /** Whether CSS size has fractional pixels */
  hasFractionalSize: boolean;
  /** Whether buffer ≠ CSS (potential DPR mismatch) */
  sizeMismatch: boolean;
  /** 2D context status */
  contextStatus: 'ok' | 'null' | 'error';
  /** Sum of alpha samples (0 = completely blank) */
  alphaSum: number;
  /** willReadFrequently hint on context */
  willReadFrequently: boolean;
}

interface DisplayInfo {
  devicePixelRatio: number;
  screenWidth: number;
  screenHeight: number;
  innerWidth: number;
  innerHeight: number;
  colorDepth: number;
  orientation: string;
  /** CSS zoom detected on <html> if set */
  documentZoom: string;
}

interface MemoryInfo {
  /** JS heap used (bytes) */
  jsHeapUsed: number | null;
  /** JS heap total (bytes) */
  jsHeapTotal: number | null;
  /** JS heap limit (bytes) */
  jsHeapLimit: number | null;
  /** usage ratio 0-1 */
  jsHeapUsageRatio: number | null;
}

interface PerformanceInfo {
  /** Navigation timing */
  pageLoadedMs: number | null;
  /** Long-task count (if PerformanceObserver available) */
  longTaskCount: number;
  /** Total number of DOM nodes */
  domNodeCount: number;
  /** Document visibility state */
  visibilityState: string;
  /** Whether document is hidden */
  documentHidden: boolean;
  /** Time since page load (ms) */
  uptimeMs: number;
  /** Whether browser reports online */
  isOnline: boolean;
}

export interface CanvasDiagnosticReport {
  timestamp: string;
  url: string;
  userAgent: string;
  canvases: CanvasInfo[];
  display: DisplayInfo;
  memory: MemoryInfo;
  performance: PerformanceInfo;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SAMPLE_GRID = 10;

const sampleAlpha = (canvas: HTMLCanvasElement): number => {
  try {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return -1;
    }
    const { width, height } = canvas;
    if (width <= 0 || height <= 0) {
      return 0;
    }
    let sum = 0;
    for (let row = 0; row < SAMPLE_GRID; row++) {
      const y = Math.floor((height * (row + 1)) / (SAMPLE_GRID + 1));
      const rowData = ctx.getImageData(0, y, width, 1).data;
      for (let col = 0; col < SAMPLE_GRID; col++) {
        const x = Math.floor((width * (col + 1)) / (SAMPLE_GRID + 1));
        sum += rowData[x * 4 + 3];
      }
    }
    return sum;
  } catch {
    return -1;
  }
};

const getCanvasInfo = (canvas: HTMLCanvasElement): CanvasInfo => {
  const rect = canvas.getBoundingClientRect();
  let contextStatus: CanvasInfo['contextStatus'] = 'ok';
  let willReadFrequently = false;
  try {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      contextStatus = 'null';
    } else {
      willReadFrequently = !!ctx.getContextAttributes?.()?.willReadFrequently;
    }
  } catch {
    contextStatus = 'error';
  }

  const dpr = window.devicePixelRatio ?? 1;
  const expectedW = Math.round(rect.width * dpr);
  const expectedH = Math.round(rect.height * dpr);

  return {
    cssWidth: rect.width,
    cssHeight: rect.height,
    bufferWidth: canvas.width,
    bufferHeight: canvas.height,
    hasFractionalSize:
      !Number.isInteger(rect.width) || !Number.isInteger(rect.height),
    sizeMismatch:
      Math.abs(canvas.width - expectedW) > 1 ||
      Math.abs(canvas.height - expectedH) > 1,
    contextStatus,
    alphaSum: sampleAlpha(canvas),
    willReadFrequently,
  };
};

const getDisplayInfo = (): DisplayInfo => ({
  devicePixelRatio: window.devicePixelRatio ?? 1,
  screenWidth: screen.width,
  screenHeight: screen.height,
  innerWidth: window.innerWidth,
  innerHeight: window.innerHeight,
  colorDepth: screen.colorDepth,
  orientation: screen.orientation?.type ?? 'unknown',
  documentZoom: getComputedStyle(document.documentElement).zoom ?? '1',
});

const getMemoryInfo = (): MemoryInfo => {
  const mem = (
    performance as {
      memory?: {
        usedJSHeapSize: number;
        totalJSHeapSize: number;
        jsHeapSizeLimit: number;
      };
    }
  ).memory;
  if (!mem) {
    return {
      jsHeapUsed: null,
      jsHeapTotal: null,
      jsHeapLimit: null,
      jsHeapUsageRatio: null,
    };
  }
  return {
    jsHeapUsed: mem.usedJSHeapSize,
    jsHeapTotal: mem.totalJSHeapSize,
    jsHeapLimit: mem.jsHeapSizeLimit,
    jsHeapUsageRatio: mem.usedJSHeapSize / mem.jsHeapSizeLimit,
  };
};

const getPerformanceInfo = (): PerformanceInfo => {
  const nav = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;

  return {
    pageLoadedMs: nav ? Math.round(nav.loadEventEnd - nav.startTime) : null,
    longTaskCount: performance.getEntriesByType('longtask').length,
    domNodeCount: document.querySelectorAll('*').length,
    visibilityState: document.visibilityState,
    documentHidden: document.hidden,
    uptimeMs: Math.round(performance.now()),
    isOnline: navigator.onLine,
  };
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Collect a full diagnostic report for every `<canvas>` element on the page.
 * Safe to call from the browser console at any time.
 */
export const collectCanvasDiagnostics = (): CanvasDiagnosticReport => {
  const canvases = Array.from(document.querySelectorAll('canvas')).map(
    getCanvasInfo,
  );

  return {
    timestamp: new Date().toISOString(),
    url: location.href,
    userAgent: navigator.userAgent,
    canvases,
    display: getDisplayInfo(),
    memory: getMemoryInfo(),
    performance: getPerformanceInfo(),
  };
};

// ---------------------------------------------------------------------------
// Global console helper
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    __canvasDiagnostics: typeof collectCanvasDiagnostics;
  }
}

if (typeof window !== 'undefined') {
  window.__canvasDiagnostics = () => {
    const report = collectCanvasDiagnostics();
    const json = JSON.stringify(report, null, 2);
    navigator.clipboard?.writeText(json)?.then(
      () => console.info('📋 Diagnostics copied to clipboard'),
      () =>
        console.warn(
          '⚠️ Could not copy — select the output above and copy manually',
        ),
    );
    return report;
  };
}
